import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import AppLayout from "../../../../../layout/DashboardLayout";
import adminIndex from "../../../components/adminIndex.module.css";
import AffiliateDialog from "../AffiliateDialog";
import PartnerLinkDialog from "../PartnerLinkDialog";
import { assignCustomer, errorText, fetchCustomers, formatDate, searchCustomers } from "../affiliateApi";
import styles from "../affiliates.module.css";

const SEARCH_DELAY = 300;

// Find a customer by name, email or store and link them to this affiliate.
function AddCustomer({ affiliateId, onLinked }) {
  const [text, setText] = useState("");
  const [results, setResults] = useState([]);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    const term = text.trim();
    if (term.length < 2) {
      setResults([]);
      return undefined;
    }
    const timer = setTimeout(() => {
      searchCustomers(term)
        .then(setResults)
        .catch(() => setResults([]));
    }, SEARCH_DELAY);
    return () => clearTimeout(timer);
  }, [text]);

  const link = async (customer) => {
    setBusyId(customer._id);
    try {
      await assignCustomer(customer._id, affiliateId);
      toast.success(`${customer.name || customer.email} linked`);
      setText("");
      onLinked();
    } catch (err) {
      toast.error(errorText(err, "Could not link the customer"));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className={styles.add}>
      <input
        id="affiliate-add-customer"
        className={styles.search}
        placeholder="Add a customer: search by name, email or store"
        value={text}
        onChange={(e) => setText(e.target.value)}
        aria-label="Search customers to link"
        autoComplete="off"
      />
      {results.length > 0 && (
        <ul className={styles.results}>
          {results.map((c) => {
            const here = c.affiliate?._id === affiliateId;
            return (
              <li key={c._id}>
                <div>
                  <b>{c.name || c.email}</b>
                  <small className={styles.muted}>
                    {c.email}
                    {c.stores.length > 0 && ` · ${c.stores.join(", ")}`}
                  </small>
                </div>
                {here ? (
                  <span className={styles.tag}>Linked</span>
                ) : (
                  <button type="button" className={styles.secondary} onClick={() => link(c)} disabled={busyId === c._id}>
                    {c.affiliate ? `Move from ${c.affiliate.name}` : "Link"}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {text.trim().length >= 2 && results.length === 0 && <p className={styles.hint}>No customers found.</p>}
    </div>
  );
}

export default function AffiliateDetail() {
  const { referralId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [sharing, setSharing] = useState(false);

  const load = useCallback(() => {
    fetchCustomers(referralId)
      .then(setData)
      .catch((err) => toast.error(errorText(err, "Could not load this affiliate")));
  }, [referralId]);

  useEffect(load, [load]);

  const unlink = async (customer) => {
    try {
      await assignCustomer(customer._id, null);
      toast.success(`${customer.name || customer.email} unlinked`);
      load();
    } catch (err) {
      toast.error(errorText(err, "Could not unlink the customer"));
    }
  };

  const customers = data?.customers || [];
  const sum = customers.reduce(
    (s, c) => ({ orders30: s.orders30 + c.orders30, ordersTotal: s.ordersTotal + c.ordersTotal, active: s.active + (c.orders30 > 0 ? 1 : 0) }),
    { orders30: 0, ordersTotal: 0, active: 0 }
  );

  return (
    <AppLayout background="#f1f1f1">
      <div className={adminIndex.page}>
        <Link to="/admin/referrals" className={styles.back}>
          ← All affiliates
        </Link>
        <div className={adminIndex.header}>
          <div>
            <h1 className={adminIndex.title}>{data?.affiliate?.name || "Affiliate"}</h1>
            {data?.affiliate?.email && <p className={styles.sub}>{data.affiliate.email}</p>}
          </div>
          {data && (
            <div className={styles.headActions}>
              <button type="button" className={styles.secondary} onClick={() => setSharing(true)}>
                Partner page
              </button>
              <button type="button" className={styles.secondary} onClick={() => setEditing(true)}>
                Edit
              </button>
            </div>
          )}
        </div>

        <div className={styles.stats}>
          <div>
            <span>Customers</span>
            <b>{data ? customers.length : "–"}</b>
          </div>
          <div>
            <span>Ordered in last 30 days</span>
            <b>{data ? sum.active : "–"}</b>
          </div>
          <div>
            <span>Orders last 30 days</span>
            <b>{data ? sum.orders30.toLocaleString("en-GB") : "–"}</b>
          </div>
          <div>
            <span>Orders total</span>
            <b>{data ? sum.ordersTotal.toLocaleString("en-GB") : "–"}</b>
          </div>
        </div>

        <div className={adminIndex.card}>
          <div className={styles.toolbar}>
            <AddCustomer affiliateId={referralId} onLinked={load} />
          </div>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Store</th>
                  <th>Phone</th>
                  <th>Linked since</th>
                  <th className={styles.num}>Orders 30 days</th>
                  <th className={styles.num}>Orders total</th>
                  <th>Last order</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {!data && (
                  <tr>
                    <td colSpan={8} className={styles.empty}>
                      Loading…
                    </td>
                  </tr>
                )}
                {data && customers.length === 0 && (
                  <tr>
                    <td colSpan={8} className={styles.empty}>
                      No customers linked yet. Search above to add one.
                    </td>
                  </tr>
                )}
                {customers.map((c) => (
                  <tr key={c._id}>
                    <td>
                      <Link className={styles.name} to={`/admin/users/user-details/${c._id}`}>
                        {c.name || c.email}
                      </Link>
                      <small className={styles.muted}>{c.email}</small>
                    </td>
                    <td>{c.stores.length ? c.stores.map((s) => s.name).join(", ") : <span className={styles.muted}>No store</span>}</td>
                    <td className={styles.nowrap}>{c.phone || <span className={styles.muted}>–</span>}</td>
                    <td className={styles.nowrap}>{formatDate(c.assignedAt)}</td>
                    <td className={styles.num}>{c.orders30.toLocaleString("en-GB")}</td>
                    <td className={styles.num}>{c.ordersTotal.toLocaleString("en-GB")}</td>
                    <td className={styles.nowrap}>{formatDate(c.lastOrderAt)}</td>
                    <td>
                      <button type="button" className={styles.link} onClick={() => unlink(c)}>
                        Unlink
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {sharing && data && (
        <PartnerLinkDialog
          affiliate={data.affiliate}
          onClose={() => setSharing(false)}
          onCreated={(shareToken) => setData((d) => ({ ...d, affiliate: { ...d.affiliate, shareToken } }))}
        />
      )}

      {editing && data && (
        <AffiliateDialog
          affiliate={data.affiliate}
          customers={customers.length}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
          onDeleted={() => navigate("/admin/referrals")}
        />
      )}
    </AppLayout>
  );
}
